<template>
  <div>
    <h4>{{ $t(this.graphTitle, { year: this.selectedYear }) }}</h4>
    <div v-if="isDataLoading" class="text-center">
      <p>{{ $t('loading') }}</p>
    </div>
    <div v-else-if="activityChartData.labels.length > 0" class="mt-2">
      <line-graph
        :chart-data="activityChartData"
        :chart-options="activityChartOptions"
      />
      <div class="row mt-4" >
        <div class="col-md-4">
          
      <h5>{{ $t('ytd-summary') }}</h5>
      <table class="table table-striped table-bordered">
        <thead>
          <tr>
            <th>{{ $t('year') }}</th>
            <th>{{ $t('total') }}</th>
            <th>{{ $t('ytd-month',{ month: months[ytdMonth] }) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(year_data, index) in ytdData" :key="index">
            <td>{{ year_data.year }}</td>
            <td>{{ year_data.total }}</td>
            <td>{{ year_data.ytd_total }}</td>
          </tr>
        </tbody>
      </table>
        </div>
      </div>
      <linear-regression
        :calculated-monthly-data="calculatedMonthlyData"
        :selected-year="selectedYear"
        :linear-windows-years="linearWindowsYears"
        :graph-title="graphTitle + '-linear-regression'"
      />
    </div>
    <div v-else>
      <p>{{ $t('no-data-available') }}</p>
    </div>
  </div>
</template>

<script>
import LineGraph from '@/components/LineGraph';
import LinearRegression from '@/components/LinearRegression';
export default {
  name: 'ActivityAndLinear',
  components: {
    LineGraph,
    LinearRegression,
  },
  data() {
    return {
      window_size: 3,
      linear_size: 5,
      colours: ['rgb(83, 140, 212)','rgb(250, 192, 142)','rgb(154, 187, 88)'],
      months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
    }
  },

  props: {
    selectedYear: String,
    dataByYear: Object,
    filterFunction: {
             type: Function,
             default: () => true // Default filter that includes all items
    },
    graphTitle: String,    
  },
  computed: {
    isDataLoading() {
      return !this.dataByYear || !this.dataByYear[this.selectedYear];
    },

    activityWindowsYears() {
        return this.calculateWindow(this.window_size);
    },

    linearWindowsYears() {
        return this.calculateWindow(this.linear_size);
    },    

    ytdData() {
        if (this.isDataLoading) {
            return [];
        }
        return this.linearWindowsYears.map((year) => {
            const month_data = this.calculatedMonthlyData[year];
            const total = month_data.reduce((a, b) => a + b, 0);
            let ytd_total = 0;
            for (let i = 0; i <= this.ytdMonth; i++) {
            ytd_total += month_data[i];
            }
            return { ytd_total: ytd_total, year: year, total: total };
        });
    },

    ytdMonth() {
        if (this.isDataLoading) {
            return '';
        }
        const last_entry = this.dataByYear[this.selectedYear][this.dataByYear[this.selectedYear].length - 1];
        return new Date(last_entry.app_start_month).getMonth();
    },

    activityChartData() {
        if (this.isDataLoading) {
            return {
            labels: [],
            datasets: [],
            };
        }
        return {
            labels: this.months,
            datasets: this.activityWindowsYears.map((year, index) => {
            return {
                label: year.toString(),
                data: this.calculatedMonthlyData[year],
                type: 'line',
                borderColor: this.colours[index],
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                tension: 0.1,
                borderWidth: year === parseInt(this.selectedYear) ? 3 : 2,
                font: {
                weight: year === parseInt(this.selectedYear) ? 'bold' : 'normal'
                },
                pointRadius: 0
            };
            }),
        };
    },
    activityChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: this.$t('volume')
            },
          },
        },
        plugins: {
        },
        legend: {
          display: true,
          position: 'top',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        },
        hover: {
          mode: 'index',
          intersect: false,
        },
        animation: {
          duration: 1000,
        },
     };
    },

    calculatedMonthlyData() {
        if (this.isDataLoading) {
            return [];
        }
        const monthlyData = {};
        this.linearWindowsYears.forEach(year => { monthlyData[year] = this.calculateMonthlyData(this.dataByYear[year], this.filterFunction)});
        return monthlyData;
    },
  },

  methods: {
    calculateMonthlyData(yearData, filterFunction) {
        if (!yearData || !Array.isArray(yearData)) {
            return Array(12).fill(0);
        }

        // Filter the data using the provided filter function
        const filteredData = yearData.filter(filterFunction);
        
        // Group and sum totals by month
        const monthlyTotals = {};
        filteredData.forEach(item => {
            const month = new Date(item.app_start_month).getMonth();
            if (!monthlyTotals[month]) {
            monthlyTotals[month] = 0;
            }
            monthlyTotals[month] += parseInt(item.total_count);
        });

        // Return array with 12 months
        const monthlyData = [];
        for (let month = 0; month < 12; month++) {
            monthlyData.push(Object.prototype.hasOwnProperty.call(monthlyTotals, month) ? monthlyTotals[month] : null);
        }

      return monthlyData;
    },

    calculateWindow(window_size) {
        if (this.isDataLoading) {
            return [];
        }
        let years = Object.keys(this.dataByYear).sort((a, b) => a - b).map(year => parseInt(year,10));
        let year = parseInt(this.selectedYear,10); 

        return (year - window_size > years[0]) ? 
            years.slice(years.indexOf(year - window_size) + 1, years.indexOf(year) + 1) : 
            years.slice(0, window_size);
    }

  },
}
</script>
